import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ModalCloseText from '../../ModalV2/ModalCloseText';
import ModalClose from '../../ModalV2/ModalClose';

export const CoreModal = React.memo(({
	className,
	showing = false,
	closeable = true,
	dialog = false,
	closeComponent: CloseComponent = ModalClose, // Use ModalClose as default
	onClose = () => {},
	children,
	wide = false,
	toolbar = null,
	size = 'medium',
	closeButtonText = null
}) => {
	const modalRef = useRef(null);
	const previousActiveElement = useRef(null);

	const handleKeyDown = (event) => {
		// Handle Escape key
		if (event.key === 'Escape' && closeable) {
			onClose();
			return;
		}

		// Handle Tab key for focus trapping
		if (event.key === 'Tab' && modalRef.current) {
			const focusableElements = modalRef.current.querySelectorAll(
				'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"]):not([disabled])'
			);

			const firstElement = focusableElements[0];
			const lastElement = focusableElements[focusableElements.length - 1];

			if (event.shiftKey) {
				// Shift + Tab: moving backwards
				if (document.activeElement === firstElement) {
					event.preventDefault();
					lastElement.focus();
				}
			} else {
				// Tab: moving forwards
				if (document.activeElement === lastElement) {
					event.preventDefault();
					firstElement.focus();
				}
			}
		}
	};

	useEffect(() => {
		if (showing) {
			document.body.classList.add('ph-new-modal-open');
		} else {
			document.body.classList.remove('ph-new-modal-open');
		}

		return () => {
			document.body.classList.remove('ph-new-modal-open');
		};
	}, [showing]);

	// Combined keyboard event handler - handles both Escape and Tab
	useEffect(() => {

		// Store the currently focused element
		previousActiveElement.current = document.activeElement;

		// Focus the modal content on open
		if (modalRef.current) {
			modalRef.current.focus();
		}

		document.addEventListener('keydown', handleKeyDown);

		// Cleanup function
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			if (previousActiveElement.current) {
				previousActiveElement.current.focus();
			}
		};
	}, []);

	// Handler for close button keyboard events
	const handleCloseKeyDown = (event) => {
		if ((event.key === 'Enter' || event.key === ' ') && closeable) {
			event.preventDefault();
			onClose();
		}
	};

	// Helper function to render the close component
	const renderCloseComponent = () => {
		if (!closeable) {
			return null;
		}

		// Text close button
		if (closeButtonText) {
			return React.createElement(
				ModalCloseText,
				{
					onClick: onClose,
					className: 'new-modal-close',
					text: closeButtonText,
					'aria-label': 'Close modal',
					'role': 'button',
					'tabIndex': 0
				}
			);
		}

		// CloseComponent is required - no fallback
		return React.createElement(
			CloseComponent,
			{
				onClick: onClose,
				onKeyDown: handleCloseKeyDown,
				className: 'new-modal-close',
				'aria-label': 'Close modal',
				'role': 'button',
				'tabIndex': 0
			}
		);
	};

	const handleOverlayClick = (event) => {
		// Only close if clicking on the overlay itself, not the modal content
		if (event.target === event.currentTarget && closeable) {
			onClose();
    	}
	};

	// Don't render anything if not showing
	if (!showing) {
		return null;
	}

	return (
		<div 
			className={classnames(className, 'ph-new-modal', {
				'ph-new-modal-fullscreen': size === 'fullscreen'
			})}
			role="dialog"
			aria-modal="true"
			aria-labelledby="modal-title"
		>
			<div className="new-modal-outer" onClick={handleOverlayClick}>
				<div
                    ref={modalRef}
					className={classnames('new-modal-content', { 
						wide,
						'size-small': size === 'small',
						'size-medium': size === 'medium',
						'size-wide': size === 'wide',
						'size-large': size === 'large',
						'size-fullscreen': size === 'fullscreen'
					})}
                    tabIndex={-1}
				>
					{toolbar}
					{renderCloseComponent()}
					{children}
				</div>
			</div>
		</div>
	);
});

CoreModal.propTypes = {
	className: PropTypes.string,
	showing: PropTypes.bool,
	closeable: PropTypes.bool,
	dialog: PropTypes.bool,
	closeComponent: PropTypes.func,
	onClose: PropTypes.func,
	children: PropTypes.node,
	wide: PropTypes.bool,
	toolbar: PropTypes.node,
	size: PropTypes.oneOf(['small', 'medium', 'wide','large', 'fullscreen']),
	closeButtonText: PropTypes.string
};

CoreModal.displayName = 'CoreModal';

export default CoreModal;
